import type { IconName } from '@/types';

/**
 * Inline SVG icon set.
 *
 * Bundled as paths rather than an icon font or sprite sheet: 24 icons cost less
 * than a network round-trip, they inherit `currentColor`, and they cannot flash
 * unstyled on first paint.
 */
const PATHS: Record<IconName, string> = {
  edit: 'M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3Z M13.5 6.5l4 4',
  grid_view: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  dashboard: 'M4 4h16v5H4zM4 12h7v8H4zM13 12h7v8h-7z',
  volume: 'M4 9v6h4l5 4V5L8 9H4Z M16.5 8.5a5 5 0 0 1 0 7 M19.5 5.5a9 9 0 0 1 0 13',
  video:
    'M3 6.5h11a1.5 1.5 0 0 1 1.5 1.5v8a1.5 1.5 0 0 1-1.5 1.5H3A1.5 1.5 0 0 1 1.5 16V8A1.5 1.5 0 0 1 3 6.5Z M15.5 10.5 21 7.5v9l-5.5-3',
  description: 'M6 3h7l5 5v13H6zM13 3v5h5 M9 13h6M9 17h6',
  check_box: 'M4 4h16v16H4zM8.5 12l2.5 2.5 4.5-5',
  schedule: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16Z M12 7.5V12l3 2',
  language:
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z M3 12h18 M12 3c2.5 2.4 3.8 5.4 3.8 9s-1.3 6.6-3.8 9c-2.5-2.4-3.8-5.4-3.8-9S9.5 5.4 12 3Z',
  done_all: 'm2 13 3.5 3.5L9 13 M7 13l3.5 3.5L21 6 M13 6l-4 4',
  receipt_long: 'M6 2.5h12v19l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5-2 1.5v-19Z M9.5 8h5M9.5 12h5',
  code: 'm8.5 8-4.5 4 4.5 4 M15.5 8l4.5 4-4.5 4 M13.5 5.5l-3 13',
  account_balance: 'M3 9.5 12 4l9 5.5 M5 10v8M9.5 10v8M14.5 10v8M19 10v8 M3 21h18',
  person: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z M4.5 20.5a7.5 7.5 0 0 1 15 0',
  star: 'm12 3.5 2.6 5.5 6 .8-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6L3.4 9.8l6-.8L12 3.5Z',
  check_circle: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM7.5 12l2.5 2.5 5-5',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14ZM16 16l4 4',
  mail: 'M3 6h18v12H3zM3 7l9 6 9-6',
  phone:
    'M7 3.5h3l1.5 4-2 1.5a12 12 0 0 0 5.5 5.5l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 5.5 5.7 2 2 0 0 1 7.5 3.5Z',
  place:
    'M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z M12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'M6 6l12 12M18 6 6 18',
  sun: 'M12 16.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Z M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8',
  moon: 'M20 14.2A8.5 8.5 0 0 1 9.8 4 8.5 8.5 0 1 0 20 14.2Z',
};

export interface IconProps {
  readonly name: IconName;
  readonly size?: number | undefined;
  /**
   * Accessible label. Omit for icons that merely decorate adjacent text — they
   * are then hidden from assistive technology, which is the correct default.
   */
  readonly title?: string | undefined;
  readonly className?: string | undefined;
  readonly strokeWidth?: number | undefined;
}

export function Icon({ name, size = 24, title, className, strokeWidth = 1.6 }: IconProps) {
  const decorative = title === undefined;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative || undefined}
      aria-label={title}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      <path d={PATHS[name]} />
    </svg>
  );
}
